import { useState, useEffect, useRef, useMemo } from 'react';
import { ApiGet } from '../Api';
import TopBar from '../TopBar';
import ReactECharts from 'echarts-for-react';
import { backgroundMainColor, backgroundSubColor } from '../Color';
import Grid from '../Grid';
import logo from './sct_logo.png';

const MainFunction = function SctMonitoring({ ChangeMonitoring, setInquiryFunction }) { 
  const gridGap = 30;
  const gridPadding = 30;
  const topBarHeight = 60;
  const gridItemStyle = {
    backgroundColor: backgroundSubColor,
    borderRadius: '10px',
    height: `calc((100vh - ${topBarHeight}px - ${gridGap}px - ${gridPadding * 2}px) / 2)`
  };

  const [gridStyle, setGridStyle] = useState(GetGridStyle());

  const chartRefs = useRef([]);  

  useEffect(() => {
    document.title = 'SCT MONITORING';

    let intervalId;

    Inquiry();
     if (ChangeMonitoring === undefined) {
      intervalId = setInterval(() => {
        Inquiry();
      }, 1000 * 60);
    } else {
      setInquiryFunction(() => ChangeMonitoring);
    }

    async function Resize() {
      setGridStyle(GetGridStyle());
    }

    window.addEventListener('resize', Resize);

    return () => {
      window.removeEventListener('resize', Resize);
      
      if (intervalId) {
        clearInterval(intervalId);
      }

      for (const chartRef of chartRefs.current) {
        if (chartRef) {
          chartRef.dispose();
        }
      }

      setGridStyle(null);
    };
  }, []);

  const [data, setData] = useState({
    minTemperature: 0,
    maxTemperature: 0,
    chartData: {},
    gridData: []
  });
  async function Inquiry() {
    const response = await ApiGet(`${process.env.REACT_APP_API_DOMAIN}sct/temperature`);
    if (response !== null && response.ok) {
      const json = await response.json();
      const newData = {
        minTemperature: json.min_temperature,
        maxTemperature: json.max_temperature,
        chartData: json.chartData,
        gridData: json.gridData
      }
      setData(newData);
    }
  }

  function OnChartReady(instance) {
    chartRefs.current.push(instance);
  }

  const option = useMemo(() => {
    const colorArray = [
      '#40E0D0',  // 터코이즈 (대체)
      '#91cc75',  // 그린
      '#fac858',  // 옐로우
      '#ee6666',  // 레드
      '#73c0de',  // 스카이블루
      '#3ba272',  // 다크그린
      '#fc8452',  // 오렌지
      '#9a60b4',  // 퍼플
      '#ea7ccc',  // 핑크
      '#22A7F0',  // 브라이트 블루
      '#BFD833',  // 라임
      '#E84A5F',  // 코랄
      '#FDE3A7',  // 바나나
      '#8D6E63',  // 브라운
      '#A2DED0',  // 민트
      '#FFFFFF'   // 화이트
    ];
    return {
      grid: {
        top: 50,
        left: 100,
        right: 150,
        bottom: 80
      },
      legend: {
        show: true,
        bottom: 10,
        right: 20,
        textStyle: {
          color: 'white',
          fontSize: 12
        },
        itemWidth: 14,
        itemHeight: 14,
        orient: 'vertical'
      },
      tooltip: {
        trigger: 'axis',
        show: false,
      },
      xAxis: {
        type: 'time',
        name: '시간',
        nameLocation: 'center',
        nameGap: 40,
        nameTextStyle: {
          fontSize: 10,
          color: 'white'
        },
        boundaryGap: false,
        axisLine: {
          lineStyle: {
            color: 'gray'
          }
        },
        axisLabel: {
          color: 'white'
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: 'gray'
          }
        }
      },
      yAxis: {
        type: 'value',
        name: '온도',
        nameLocation: 'center',
        nameGap: 50,
        nameTextStyle: {
          fontSize: 10,
          color: 'white'
        },
        min: data.minTemperature - 1,
        max: data.maxTemperature + 1,
        axisLine: {
          lineStyle: {
            color: 'gray'
          }
        },
        axisLabel: {
          color: 'white',
          formatter: '{value}ºC'
        },
        splitLine: {
          lineStyle: {
            color: 'gray'
          }
        }
      },
      series: Object.entries(data.chartData).map(([key, value], index) => {
          return {
            name: key,
            type: 'line',
            smooth: true,
            symbol: 'none',
            symbolSize: 8,
            lineStyle: {
              color: colorArray[index],
              width: 3
            },
            itemStyle: {
              color: colorArray[index],
              //borderColor: 'white',
              //borderWidth: 1
            },
            data: value
          };
        })
    };
  }, [data]);

  function GetGridStyle() {
    return {
      display: 'grid',
      gap: gridGap,
      padding: gridPadding,
      backgroundColor: backgroundMainColor,
      width: '100vw',
      height: `calc(100vh - ${topBarHeight}px)`,
      boxSizing: 'border-box', //패딩을 포함하여 크기 계산
      overflow: 'auto',
      scrollbarWidth: 'none',
      gridTemplateColumns: '1fr'
    };
  }

  const columnDictionary = {
    'cellName': { label: '셀 목록' },
    'h00': { label: '00' },
    'h01': { label: '01' },
    'h02': { label: '02' },
    'h03': { label: '03' },
    'h04': { label: '04' },
    'h05': { label: '05' },
    'h06': { label: '06' },
    'h07': { label: '07' },
    'h08': { label: '08' },
    'h09': { label: '09' },
    'h10': { label: '10' },
    'h11': { label: '11' },
    'h12': { label: '12' },
    'h13': { label: '13' },
    'h14': { label: '14' },
    'h15': { label: '15' },
    'h16': { label: '16' },
    'h17': { label: '17' },
    'h18': { label: '18' },
    'h19': { label: '19' },
    'h20': { label: '20' },
    'h21': { label: '21' },
    'h22': { label: '22' },
    'h23': { label: '23' }
  };

  const logoImg = <img src={logo} width={'100px'} height={'47px'} style={{ marginLeft: '30px' }} />;

  return (
    <div onContextMenu={(event)=>{ event.preventDefault(); }} style={{ userSelect: 'none', width: '100vw', height: '100vh', backgroundColor: backgroundMainColor }}>
      <TopBar logo={logoImg} title={'온도 현황'} />

      <div style={gridStyle}>
        <div style={gridItemStyle}>
          <ReactECharts option={option} style={{ width: '100%', height: '100%' }} onChartReady={OnChartReady}/>
        </div>

        <div style={gridItemStyle}>
          <Grid cellData={data.gridData} columnDictionary={columnDictionary} inquiryInterval={60} />
        </div>
      </div>
    </div>
  );
}

export default MainFunction;